import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCreatePartyMutation } from "Api/Mutations/Party/useCreatePartyMutation";
import { useCreatePartyRelationMutation } from "Api/Mutations/Party/useCreatePartyRelationMutation";
import { useUploadIdentityDocuments } from "Api/Mutations/Onboarding/useUploadIdentityDocuments";
import { expiringRelatedPartiesKeys } from "Api/Queries/Party/useExpiringRelatedPartiesQuery";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormSection } from "Components/Shared/FormSection";
import {
  AddressUncommittedProvider,
  useAddressUncommittedTracker,
} from "Components/Shared/Inputs/Form/AddressUncommittedContext";
import { SecondDocumentTypes } from "Components/Onboarding/shared/DocumentsUpload/types";
import {
  EMPTY_DOCUMENTS,
  StatutoryBodyDocumentsFields,
  type StatutoryBodyDocuments,
} from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/Add/StatutoryBodyDocumentsFields";
import { RelatedPartyFormFields } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartyFormFields";
import { StyledForm } from "Components/Users/UserDetailPageContent/Components/StyledForm";
import {
  createRelatedPartyValidationSchema,
  type RelatedPartyFormData,
  type RelatedPartyFormDataInput,
} from "Components/Users/schemas";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import { isBusinessRelationType } from "Models/BusinessRelationTypes";
import {
  AddressType,
  type AddressDto,
  type ApiProblemDetails,
  type CreatePartyDto,
  Gender,
  PartyType,
  RelationType,
} from "Infrastructure/Api/Api";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { NO_VAT_NUMBER } from "Schemas/vatNumberSchema";
import { Resources, useResource } from "Translations/Resources";
import { EMPTY_PHONE } from "Utils/PhoneUtils";

const ADD_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties.Add;

const NEW_PARTY_ADDRESSES: Array<AddressDto> = [
  { type: AddressType.Permanent },
];

type Props = {
  owningPartyID: string;
  relationType: RelationType;
  onSuccess: () => void;
};

export const AddBusinessRolePartyForm = ({
  owningPartyID,
  relationType,
  onSuccess,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documents, setDocuments] =
    useState<StatutoryBodyDocuments>(EMPTY_DOCUMENTS);
  const { t } = useResource();
  const queryClient = useQueryClient();
  const isCurrentUserMerchant = useIsCurrentUserMerchant();
  const { hasUncommittedAddress, uncommittedContextValue } =
    useAddressUncommittedTracker();

  const schema = createRelatedPartyValidationSchema(
    t,
    PartyType.NaturalPerson,
    {
      isResidenceRequired: isBusinessRelationType(relationType),
    },
  );
  const formMethods = useForm<
    RelatedPartyFormDataInput,
    unknown,
    RelatedPartyFormData
  >({
    resolver: zodResolver(schema),
    defaultValues: {
      publicID: "",
      type: PartyType.NaturalPerson,
      gender: Gender.NotSet,
      personalNumber: "",
      vatNumber: NO_VAT_NUMBER,
      phone: EMPTY_PHONE,
      isForeigner: false,
      isOwner: false,
      relationType,
      addresses: [{ type: AddressType.Permanent }],
      licenses: [],
      relatedParties: [],
      partyIdentification: {},
    },
  });
  const { control, handleSubmit } = formMethods;

  const { mutateAsync: createPartyAsync } = useCreatePartyMutation();
  const { mutateAsync: createRelationAsync } = useCreatePartyRelationMutation();
  const { mutateAsync: uploadDocumentsAsync } = useUploadIdentityDocuments({
    successMessage: t(ADD_RESOURCES.AddSuccess),
  });

  const canUploadDocuments =
    isCurrentUserMerchant && relationType === RelationType.StatutoryBody;

  const getDocumentsError = (): string | null => {
    if (!canUploadDocuments) {
      return null;
    }
    const { firstFront, firstBack, secondType, secondFront, secondBack } =
      documents;
    if (!firstFront || !firstBack || !secondType || !secondFront) {
      return t(ADD_RESOURCES.DocumentsIncomplete);
    }
    if (secondType === SecondDocumentTypes.DrivingLicense && !secondBack) {
      return t(ADD_RESOURCES.DocumentsIncomplete);
    }
    return null;
  };

  // Holds a party that was created but not yet linked to the company. If linking
  // fails, we keep its publicID so pressing submit again reuses that party instead
  // of orphaning a second one. Cleared once both steps succeed.
  const createdPartyPublicIDRef = useRef<string | null>(null);

  type CreatePartyWithRelationResult =
    | { status: "success"; partyPublicID: string }
    | { status: "partyFailed" }
    | { status: "linkFailed" }
    | { status: "residenceRequired" };

  const createPartyWithRelation = async (
    payload: CreatePartyDto,
  ): Promise<CreatePartyWithRelationResult> => {
    let partyPublicID = createdPartyPublicIDRef.current;

    // Step 1: create the party (skipped when a previous attempt already created it).
    if (!partyPublicID) {
      try {
        const created = await createPartyAsync(payload);
        partyPublicID = created.publicID ?? null;
      } catch {
        return { status: "partyFailed" };
      }
      if (!partyPublicID) {
        return { status: "partyFailed" };
      }
      // Persist before linking so a failed link can be retried without duplicating.
      createdPartyPublicIDRef.current = partyPublicID;
    }

    // Step 2: link the party to the owning company.
    try {
      await createRelationAsync({
        partyPublicID: owningPartyID,
        data: { relationType, relatedPartyPublicID: partyPublicID },
      });
    } catch (error) {
      const apiErrors = (error as { data?: ApiProblemDetails })?.data?.errors;
      return apiErrors && "RelatedPartyPublicID" in apiErrors
        ? { status: "residenceRequired" }
        : { status: "linkFailed" };
    }

    // Both steps succeeded — clear so a later add starts fresh.
    createdPartyPublicIDRef.current = null;
    return { status: "success", partyPublicID };
  };

  const onSubmit = async (data: RelatedPartyFormData) => {
    if (hasUncommittedAddress) {
      toast.error(t(Resources.Components.Shared.AddressInput.UncommittedQuery));
      return;
    }

    const documentsError = getDocumentsError();
    if (documentsError) {
      toast.error(documentsError);
      return;
    }

    setIsSubmitting(true);

    const result = await createPartyWithRelation({
      ...data,
      type: PartyType.NaturalPerson,
    });

    if (result.status === "partyFailed") {
      toast.error(t(ADD_RESOURCES.AddError));
      setIsSubmitting(false);
      return;
    }

    if (result.status === "residenceRequired") {
      // Person created but not linked — its ID is retained, so completing the address and
      // submitting again finishes the link instead of creating a second person.
      toast.error(t(Resources.Validation.ResidenceRequired));
      setIsSubmitting(false);
      return;
    }

    if (result.status === "linkFailed") {
      // Person created but not linked — its ID is retained so a retry finishes the link.
      toast.error(t(ADD_RESOURCES.LinkError));
      setIsSubmitting(false);
      return;
    }

    const newPartyPublicID = result.partyPublicID;

    const { firstFront, firstBack, secondFront, secondBack, secondType } =
      documents;

    try {
      if (canUploadDocuments && firstFront && firstBack) {
        await uploadDocumentsAsync({
          partyPublicID: newPartyPublicID,
          documents: {
            firstIDCardFrontSide: firstFront,
            firstIDCardBackSide: firstBack,
            secondIDCardFrontSide: secondFront ?? undefined,
            secondIDCardBackSide:
              secondType === SecondDocumentTypes.DrivingLicense
                ? (secondBack ?? undefined)
                : undefined,
            secondDocumentType: secondType || undefined,
          },
        });
      } else {
        toast.success(t(ADD_RESOURCES.AddSuccess));
      }
    } catch {
      // useUploadIdentityDocuments surfaces its own error toast; the party + relation were created,
      // so the documents can still be uploaded later via re-upload.
    } finally {
      queryClient.invalidateQueries({
        queryKey: getPartyQueryKey(owningPartyID),
      });
      queryClient.invalidateQueries({
        queryKey: expiringRelatedPartiesKeys.all,
      });
      onSuccess();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <AddressUncommittedProvider value={uncommittedContextValue}>
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <RelatedPartyFormFields
            control={control}
            addresses={NEW_PARTY_ADDRESSES}
          />
          {canUploadDocuments && (
            <FormSection legend={t(ADD_RESOURCES.DocumentsTitle)}>
              <StatutoryBodyDocumentsFields
                value={documents}
                onChange={setDocuments}
              />
            </FormSection>
          )}
          <PrimaryButton
            fullWidth
            type="submit"
            isLoading={isSubmitting}
            variant="contained"
            color="primary"
          >
            {t(ADD_RESOURCES.Submit)}
          </PrimaryButton>
        </StyledForm>
      </AddressUncommittedProvider>
    </FormProvider>
  );
};
